// Key Insights (Range Bars + Distribution cards) — computed from the same
// costBandData rows already fetched once per role by
// JobDetailTerritoryQuarterRow.jsx (the /api/cost-band-detail endpoint,
// itself porting GetCompensationOverview.sql). No new endpoint/table - same
// data already driving Cost Band Detail, Range Bars, and Distribution.
//
// No old-code precedent exists for this feature (grepped the entire old
// Angular codebase for insight/cost-zone/clustering/premium/savings/anchor-
// territory concepts - zero matches). This is a new build guided by the
// newer prototype dashboard's screenshots, not a port - same situation as
// the Distribution chart itself. Flagging that plainly since it deviates
// from this project's normal "port, don't invent" rule, by necessity.
//
// Each returned string may contain **double-asterisk** spans - InsightCard.jsx
// renders those as highlighted (the prototype's dark-blue dynamic-number
// styling), everything else as plain text.

import { percentilesFor, fitGaussianSafe } from "./distributionStats";

const TERRITORY_NAMES = {
  4: "the UK",
  5: "Ireland",
  2: "South Africa",
  3: "Romania",
  1: "India",
  6: "Brazil",
};
const TERRITORY_NAMES_SHORT = {
  4: "UK",
  5: "Ireland",
  2: "South Africa",
  3: "Romania",
  1: "India",
  6: "Brazil",
};
function name(d) {
  return TERRITORY_NAMES[d.territory_id] || d.territory || "this territory";
}
function nameShort(d) {
  return TERRITORY_NAMES_SHORT[d.territory_id] || d.territory || "this territory";
}

function fmtGBP(n) {
  return "£" + Math.round(n).toLocaleString("en-GB");
}
function fmtK(n) {
  const k = n / 1000;
  return "£" + (Number.isInteger(k) ? k : k.toFixed(1)) + "k";
}

// --- Range Bars card -------------------------------------------------------

// 1. Cross-territory Exp ratio - which territory sets the premium.
function insightExpPremium(data) {
  const rows = data.filter((d) => d.exp != null);
  if (rows.length < 2) return null;

  const premium = rows.reduce((a, b) => (b.exp > a.exp ? b : a));
  const cheapest = rows.reduce((a, b) => (b.exp < a.exp ? b : a));
  if (premium === cheapest) return null;

  const ratioVsCheapest = premium.exp / cheapest.exp;
  const uk = rows.find((d) => d.territory_id === 4);
  const isUkPremium = premium.territory_id === 4;

  if (isUkPremium) {
    return `An experienced hire in **${nameShort(premium)}** costs **${ratioVsCheapest.toFixed(2)}×** the same hire in **${nameShort(cheapest)}**; the UK sets the premium for this role.`;
  }

  const ratioVsUk = uk && uk.territory_id !== premium.territory_id && uk.exp ? premium.exp / uk.exp : null;
  const vsUkPart = ratioVsUk ? ` and **${ratioVsUk.toFixed(2)}×** the UK equivalent` : "";
  return `An experienced hire in **${nameShort(premium)}** costs **${ratioVsCheapest.toFixed(2)}×** the same hire in **${nameShort(cheapest)}**${vsUkPart}; for this role, **${nameShort(premium)}** sets the premium, not the UK.`;
}

// 2. Core hire band (NTR→Vexp) spread - widest vs narrowest.
function insightCoreBandSpread(data) {
  const rows = data.filter((d) => d.core_band_spread != null);
  if (rows.length < 2) return null;

  const widest = rows.reduce((a, b) => (b.core_band_spread > a.core_band_spread ? b : a));
  const narrowest = rows.reduce((a, b) => (b.core_band_spread < a.core_band_spread ? b : a));
  if (widest === narrowest) return null;

  return `The core hire band (NTR→Vexp) is widest in **${nameShort(widest)}** at **${fmtGBP(widest.core_band_spread)}**, and remarkably tight in **${nameShort(narrowest)}** at just **${fmtGBP(narrowest.core_band_spread)}**. ${nameShort(narrowest)} shows very little pay differentiation between new and very experienced staff at this level.`;
}

// 3. Cost-zone clustering by Exp value. No stakeholder-defined threshold
// exists anywhere (not in old code, not visible in the prototype
// screenshots) - using a 25%-relative-gap rule as a reasonable default.
// Adjust CLUSTER_GAP_RATIO if the real threshold differs.
const CLUSTER_GAP_RATIO = 0.25;
function insightCostClusters(data) {
  const rows = data.filter((d) => d.exp != null).slice().sort((a, b) => a.exp - b.exp);
  if (rows.length < 2) return null;

  const clusters = [[rows[0]]];
  for (let i = 1; i < rows.length; i++) {
    const prev = rows[i - 1];
    const gapRatio = (rows[i].exp - prev.exp) / prev.exp;
    if (gapRatio > CLUSTER_GAP_RATIO) clusters.push([rows[i]]);
    else clusters[clusters.length - 1].push(rows[i]);
  }
  if (clusters.length < 2) return null;

  const parts = clusters.map((c) => {
    const names = c.map(nameShort).join(" & ");
    const lo = Math.min(...c.map((d) => d.exp));
    const hi = Math.max(...c.map((d) => d.exp));
    const range = lo === hi ? fmtK(lo) : `${fmtK(lo)}-${fmtK(hi)}`;
    return `**${names}** at **${range}**`;
  });

  const overallLo = rows[0].exp;
  const overallHi = rows[rows.length - 1].exp;
  const spread = overallLo > 0 ? overallHi / overallLo : null;

  const zoneWord = clusters.length === 2 ? "Two" : clusters.length === 3 ? "Three" : `${clusters.length}`;
  const spreadPart = spread ? `, a **${spread.toFixed(1)}×** spread across the operating footprint.` : ".";
  return `${zoneWord} distinct cost zones: ${parts.join(", ")}${spreadPart}`;
}

// 4. Max (per territory) vs UK Exp - undercutting territories + savings range.
function insightMaxVsUkExp(data) {
  const uk = data.find((d) => d.territory_id === 4 && d.exp != null);
  if (!uk) return null;

  const cheaper = data.filter((d) => d.territory_id !== 4 && d.max != null && d.max < uk.exp);
  if (cheaper.length === 0) return null;

  const others = data.filter((d) => d.territory_id !== 4 && d.max != null);
  const savings = cheaper.map((d) => uk.exp - d.max);
  const minSave = Math.min(...savings);
  const maxSave = Math.max(...savings);
  const names = cheaper.map(nameShort).join(", ");
  const lowN = cheaper.filter((d) => d.low_n);
  const lowNNote = lowN.length
    ? ` Note: ${lowN.map((d) => `${nameShort(d)}'s pattern is based on a small sample (n=${d.full_range_ee_count})`).join("; ")}.`
    : "";

  return `In **${cheaper.length}** of **${others.length}** other territories (**${names}**), even a top-of-band Max hire costs less than a typical UK Exp hire, with savings of **${fmtK(minSave)}-${fmtK(maxSave)}**. The arbitrage holds at the local pay ceiling, not just the floor.${lowNNote}`;
}

const RANGE_INSIGHT_FALLBACK =
  "Not enough territory data selected to compute this yet. Pick more territories to see this insight.";

export function computeRangeInsights(data) {
  if (!data || data.length === 0) {
    return [RANGE_INSIGHT_FALLBACK, RANGE_INSIGHT_FALLBACK, RANGE_INSIGHT_FALLBACK, RANGE_INSIGHT_FALLBACK];
  }
  return [
    insightExpPremium(data) || RANGE_INSIGHT_FALLBACK,
    insightCoreBandSpread(data) || RANGE_INSIGHT_FALLBACK,
    insightCostClusters(data) || RANGE_INSIGHT_FALLBACK,
    insightMaxVsUkExp(data) || RANGE_INSIGHT_FALLBACK,
  ];
}

// --- Distribution card ------------------------------------------------------

const DIST_METHODOLOGY_TEXT =
  "Curves are fitted to stated percentiles, not assumed: the peak (μ) is where the typical incumbent actually sits, not a guessed midpoint.";

// 2. Largest-sample territory - fitted sigma + n.
function insightLargestSample(data) {
  const rows = data.filter((d) => d.full_range_ee_count > 0);
  if (rows.length === 0) return null;

  const largest = rows.reduce((a, b) => (b.full_range_ee_count > a.full_range_ee_count ? b : a));
  const fit = fitGaussianSafe(largest);
  const sigmaPart = fit ? `**σ≈${fmtK(fit.sigma)}** fitted to ` : "";
  return `**${name(largest)}**'s distribution is striking: ${sigmaPart}**n=${largest.full_range_ee_count.toLocaleString("en-GB")}** incumbents produces the tallest, narrowest, most confident curve on the chart.`;
}

// 3. Synthetic / shifted-schema / low-n territories.
function insightCaveats(data) {
  const synthetic = data.filter((d) => d.synthetic);
  const shifted = data.filter((d) => percentilesFor(d.territory_id).exp !== 50);
  const lowN = data.filter((d) => d.low_n);

  if (synthetic.length === 0 && shifted.length === 0 && lowN.length === 0) return null;

  const parts = [];
  if (synthetic.length) {
    parts.push(
      `**${synthetic.map(nameShort).join(", ")}** ${synthetic.length === 1 ? "has" : "have"} no reported incumbents; its bands are synthetic, derived from approved business rules. Shown as a dashed range bar; no distribution is inferred.`
    );
  }
  const shiftedNotLowN = shifted.filter((d) => !lowN.includes(d));
  const lowNList = lowN.map((d) => `${nameShort(d)}${shifted.includes(d) ? " (shifted schema)" : ""} (n=${d.full_range_ee_count})`);
  if (lowN.length) {
    parts.push(`**${lowNList.join(", ")}** ${lowN.length === 1 ? "uses" : "use"} a small sample, so ${lowN.length === 1 ? "its" : "their"} fitted curve should be read with caution.`);
  }
  if (shiftedNotLowN.length) {
    parts.push(`**${shiftedNotLowN.map(nameShort).join(", ")}** ${shiftedNotLowN.length === 1 ? "uses" : "use"} a shifted schema (Exp labelled P${percentilesFor(shiftedNotLowN[0].territory_id).exp} rather than P50).`);
  }
  return parts.join(" ");
}

// 4. Smallest-sample territory - inverse of insightLargestSample, plus the
// √n height-scaling note (nFactor/maxNFor - confirmed already implemented
// in DistributionChart.jsx's rendering, not aspirational).
function insightSmallestSample(data) {
  const rows = data.filter((d) => d.full_range_ee_count > 0);
  if (rows.length < 2) return null;

  const largest = rows.reduce((a, b) => (b.full_range_ee_count > a.full_range_ee_count ? b : a));
  const smallest = rows.reduce((a, b) => (b.full_range_ee_count < a.full_range_ee_count ? b : a));
  if (largest === smallest) return null;

  return `Curve height is scaled by **√n**, so **${name(largest)}** (**n=${largest.full_range_ee_count.toLocaleString("en-GB")}**) dominates visually while **${name(smallest)}** sits shortest (**n=${smallest.full_range_ee_count.toLocaleString("en-GB")}**). The same headline pay number deserves more caveats from a small sample; the visual encodes statistical confidence directly.`;
}

const DIST_INSIGHT_FALLBACK =
  "Not enough territory data selected to compute this yet. Pick more territories to see this insight.";

export function computeDistributionInsights(data) {
  if (!data || data.length === 0) {
    return [DIST_METHODOLOGY_TEXT, DIST_INSIGHT_FALLBACK, DIST_INSIGHT_FALLBACK, DIST_INSIGHT_FALLBACK];
  }
  return [
    DIST_METHODOLOGY_TEXT,
    insightLargestSample(data) || DIST_INSIGHT_FALLBACK,
    insightCaveats(data) || "No synthetic, shifted-schema, or small-sample territories in this selection.",
    insightSmallestSample(data) || DIST_INSIGHT_FALLBACK,
  ];
}
